import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Nullable } from "../../pojo/Util";
import { AlertService } from "../../services/alert.service";
import { TelemetryService } from "../../services/telemetry.service";
import { UserStore } from "../../stores/user.store";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { TrailProgressTreeComponent } from "../student-engagement/trails/trail.progress.tree.component";
import {
    TrailRequirementTreeComponent,
    wireTreeToEditorNode,
} from "../student-engagement/trails/trail.requirement.tree.component";
import { formatUserprefDate } from "../student-engagement/trails/trail.userpref.date";
import {
    AcademicCohort,
    AcademicCourse,
    AcademicCurricApi,
    AcademicProgram,
    AcademicTerm,
    TreeMergeConflict,
    TreeMergeResolutionPolicy,
} from "./academic.api";
import { AcademicLabels } from "./academic.labels";
import { AcademicWorkbookSpreadsheetUtil } from "./academic.workbook.spreadsheet.util";

/** Lets the contact dropdown finish closing before the student panels re-render. */
export const STUDENT_REFRESH_DELAY_MS = 200;

const MERGE_FIELD_LABELS: Record<string, string> = {
    name: "Name",
    groupType: "Operator",
    nOfThreshold: "N required",
    minCredits: "Min credits",
    maxCredits: "Max credits",
    leafType: "Requirement type",
    courseId: "Course",
    minGrade: "Min grade",
    isCorequisite: "Corequisite",
    disallowPassNoPass: "Pass / no-pass",
    isProhibited: "Prohibited",
    subject: "Subject",
    levelMin: "Level min",
    levelMax: "Level max",
    externalCode: "Fact",
    requiredValue: "Required value",
    minNumericValue: "Min numeric",
    excludeCourseIds: "Excluded courses",
    node: "Requirement",
};

@TypeManagerDecorator("s25-ng-academic-home")
@Component({
    selector: "s25-ng-academic-home",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25DropdownPaginatedModule,
        TrailRequirementTreeComponent,
        TrailProgressTreeComponent,
    ],
    host: { class: "academic-home" },
    template: `
        <div class="academic-home__page">
            <div class="academic-home__header">
                <div class="academic-home__header-copy">
                    <h1>{{ labels.CURRICULUM }}</h1>
                    <p class="c-text--secondary">
                        Terms, academic cohorts, courses, and program trees. Upload a workbook to load structure.
                        Nothing on this page edits a course or a program.
                    </p>
                </div>
                <div class="academic-home__actions">
                    <s25-ng-button [type]="'outline'" [onClick]="downloadTemplate">Download template</s25-ng-button>
                    <s25-ng-button [type]="'outline'" [onClick]="downloadWorkbook">Download workbook</s25-ng-button>
                    <s25-ng-button [type]="'outline'" [onClick]="openWorkbookPicker">Upload workbook</s25-ng-button>
                    <input
                        #workbookUpload
                        class="academic-home__upload-input"
                        type="file"
                        accept=".xlsx"
                        data-id="workbook-upload"
                        (change)="onWorkbookSelected($event)"
                    />
                </div>
            </div>
            @if (importErrors().length) {
                <ul class="c-text--error c-margin-bottom--single" data-id="import-errors">
                    @for (error of importErrors(); track error) {
                        <li>{{ error }}</li>
                    }
                </ul>
            }
            @if (importMessage()) {
                <p class="c-text--success c-margin-bottom--single" data-id="import-message">
                    {{ importMessage() }}
                </p>
            }

            <section class="academic-home__section">
                <div class="academic-home__section-heading">
                    <h2>Workbook merge</h2>
                </div>
                <label class="academic-home__resolution">
                    {{ labels.MERGE_RESOLUTION }} *
                    <select
                        class="c-input"
                        data-id="merge-resolution"
                        [ngModel]="resolutionChoice()"
                        (ngModelChange)="onResolutionChange($event)"
                    >
                        <option value="REVIEW">{{ labels.MERGE_REVIEW }}</option>
                        <option value="OURS">{{ labels.MERGE_OURS }}</option>
                        <option value="SIS">{{ labels.MERGE_SIS }}</option>
                    </select>
                </label>
            </section>

            <section class="academic-home__section">
                <div class="academic-home__section-heading">
                    <h2>Terms</h2>
                </div>
                @if (termsResource.isLoading()) {
                    <p>Loading terms…</p>
                } @else if (!sortedTerms().length) {
                    <p class="c-text--secondary">No terms yet. Add one or upload a workbook.</p>
                } @else {
                    <table class="c-table">
                        <thead>
                            <tr>
                                <th>Term code</th>
                                <th>Description</th>
                                <th>Start</th>
                                <th>End</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (term of sortedTerms(); track term.termId) {
                                <tr
                                    [class.academic-home__row--selected]="selectedTermId() === term.termId"
                                    (click)="selectTerm(term.termId)"
                                >
                                    <td>{{ term.termCode }}</td>
                                    <td>{{ term.description ?? "—" }}</td>
                                    <td>{{ dateLabel(term.startDt) }}</td>
                                    <td>{{ dateLabel(term.endDt) }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
                <div class="academic-home__term-form c-margin-top--single">
                    <label>
                        Term code *
                        <input
                            class="c-input"
                            [ngModel]="newTermCode()"
                            (ngModelChange)="newTermCode.set($event)"
                            required
                        />
                    </label>
                    <label>
                        Description
                        <input
                            class="c-input"
                            [ngModel]="newTermDescription()"
                            (ngModelChange)="newTermDescription.set($event)"
                        />
                    </label>
                    <label>
                        Start *
                        <input
                            class="c-input"
                            type="date"
                            [ngModel]="newTermStart()"
                            (ngModelChange)="newTermStart.set($event)"
                            required
                        />
                    </label>
                    <label>
                        End *
                        <input
                            class="c-input"
                            type="date"
                            [ngModel]="newTermEnd()"
                            (ngModelChange)="newTermEnd.set($event)"
                            required
                        />
                    </label>
                    <s25-ng-button [type]="'primary'" [onClick]="addTerm">Add term</s25-ng-button>
                </div>
            </section>

            <section class="academic-home__section">
                <h2>Academic cohorts</h2>
                @if (!cohorts().length) {
                    <p class="c-text--secondary">No academic cohorts yet. Upload a workbook to load catalogs.</p>
                } @else {
                    <table class="c-table">
                        <thead>
                            <tr>
                                <th>Cohort code</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (cohort of cohorts(); track cohort.cohortId) {
                                <tr
                                    [class.academic-home__row--selected]="selectedCohortId() === cohort.cohortId"
                                    (click)="selectCohort(cohort.cohortId)"
                                >
                                    <td>{{ cohort.cohortCode }}</td>
                                    <td>{{ cohort.cohortTypeCode }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </section>

            <section class="academic-home__section">
                <h2>Courses</h2>
                @if (!selectedCohortId()) {
                    <p class="c-text--secondary">Select a cohort to see its courses.</p>
                } @else if (!courses().length) {
                    <p class="c-text--secondary">No courses in this cohort.</p>
                } @else {
                    <table class="c-table">
                        <thead>
                            <tr>
                                <th>Course code</th>
                                <th>Subject</th>
                                <th>Level</th>
                                <th>Title</th>
                                <th>Credits</th>
                                <th>Distinct topic</th>
                                <th>Status</th>
                                <th>SIS Id</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (course of courses(); track course.courseId) {
                                <tr
                                    [class.academic-home__row--selected]="selectedCourseId() === course.courseId"
                                    (click)="selectCourse(course.courseId)"
                                >
                                    <td>{{ course.courseCode }}</td>
                                    <td>{{ course.subject ?? "—" }}</td>
                                    <td>{{ course.level ?? "—" }}</td>
                                    <td>{{ course.title ?? "—" }}</td>
                                    <td>{{ course.creditMin ?? "—" }}–{{ course.creditMax ?? "—" }}</td>
                                    <td>{{ course.distinctTopic ? "Yes" : "No" }}</td>
                                    <td>{{ course.status ?? "—" }}</td>
                                    <td>{{ course.sisId ?? "—" }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </section>

            <section class="academic-home__section">
                <h2>Programs</h2>
                @if (!selectedCohortId()) {
                    <p class="c-text--secondary">Select a cohort to see its programs.</p>
                } @else if (!programs().length) {
                    <p class="c-text--secondary">No programs in this cohort.</p>
                } @else {
                    <table class="c-table">
                        <thead>
                            <tr>
                                <th>Program</th>
                                <th>Kind</th>
                                <th>SIS Id</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (program of programs(); track program.defnId) {
                                <tr
                                    [class.academic-home__row--selected]="selectedProgramId() === program.defnId"
                                    (click)="selectProgram(program.defnId)"
                                >
                                    <td>{{ program.displayName || program.name || program.endingAwardKey }}</td>
                                    <td>{{ program.programKind ?? "—" }}</td>
                                    <td>{{ program.sisId ?? "—" }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </section>

            <section class="academic-home__section">
                <h2>Requirement trees</h2>
                <div class="academic-home__trees">
                    <div>
                        <h3>Course requisites</h3>
                        @if (courseEvalTree(); as evalTree) {
                            <s25-ng-trail-progress-tree [node]="evalTree" [courses]="courses()" />
                        } @else if (courseEditorTree(); as editorTree) {
                            <s25-ng-trail-requirement-tree
                                [node]="editorTree"
                                [editable]="false"
                                [domain]="'ACADEMIC'"
                                [courses]="courses()"
                            />
                        } @else {
                            <p class="c-text--secondary">Select a course with a requisite tree.</p>
                        }
                        @if (courseConflicts().length) {
                            <h4>{{ labels.MERGE_CONFLICTS }}</h4>
                            <table class="c-table" data-id="course-conflicts">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Ours</th>
                                        <th>Upload</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (conflict of courseConflicts(); track conflict.conflictId) {
                                        <tr>
                                            <td>{{ fieldLabel(conflict.fieldName) }}</td>
                                            <td>{{ conflict.storedValue ?? "—" }}</td>
                                            <td>{{ conflict.incomingValue ?? "—" }}</td>
                                            <td class="academic-home__conflict-actions">
                                                <s25-ng-button [type]="'outline'" [onClick]="keepOursFor(conflict)">
                                                    {{ labels.MERGE_KEEP_OURS }}
                                                </s25-ng-button>
                                                <s25-ng-button [type]="'outline'" [onClick]="takeUploadFor(conflict)">
                                                    {{ labels.MERGE_TAKE_UPLOAD }}
                                                </s25-ng-button>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        }
                    </div>
                    <div>
                        <h3>Program requirements</h3>
                        @if (programEvalTree(); as evalTree) {
                            <s25-ng-trail-progress-tree [node]="evalTree" [courses]="courses()" />
                        } @else if (programEditorTree(); as editorTree) {
                            <s25-ng-trail-requirement-tree
                                [node]="editorTree"
                                [editable]="false"
                                [domain]="'ACADEMIC'"
                                [courses]="courses()"
                            />
                        } @else {
                            <p class="c-text--secondary">Select a program with a requirement tree.</p>
                        }
                        @if (programConflicts().length) {
                            <h4>{{ labels.MERGE_CONFLICTS }}</h4>
                            <table class="c-table" data-id="program-conflicts">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Ours</th>
                                        <th>Upload</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (conflict of programConflicts(); track conflict.conflictId) {
                                        <tr>
                                            <td>{{ fieldLabel(conflict.fieldName) }}</td>
                                            <td>{{ conflict.storedValue ?? "—" }}</td>
                                            <td>{{ conflict.incomingValue ?? "—" }}</td>
                                            <td class="academic-home__conflict-actions">
                                                <s25-ng-button [type]="'outline'" [onClick]="keepOursFor(conflict)">
                                                    {{ labels.MERGE_KEEP_OURS }}
                                                </s25-ng-button>
                                                <s25-ng-button [type]="'outline'" [onClick]="takeUploadFor(conflict)">
                                                    {{ labels.MERGE_TAKE_UPLOAD }}
                                                </s25-ng-button>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        }
                    </div>
                </div>
            </section>

            <section class="academic-home__section">
                <h2>Student</h2>
                <div class="academic-home__student-picker">
                    <label class="academic-home__student-label">
                        Student
                        <s25-contact-dropdown
                            [value]="selectedContact()"
                            (valueChange)="onStudentSelected($event)"
                            [placeholder]="'Search a student'"
                        />
                    </label>
                    @if (selectedContact()) {
                        <s25-ng-button [type]="'outline'" [onClick]="clearStudent" data-id="clear-student">
                            Clear student
                        </s25-ng-button>
                    }
                </div>
                @if (selectedContId() != null) {
                    <div class="academic-home__student-cols">
                        <div>
                            <h3>Completions</h3>
                            @if (!completions().length) {
                                <p class="c-text--secondary">No completions.</p>
                            } @else {
                                <table class="c-table">
                                    <thead>
                                        <tr>
                                            <th>Subject</th>
                                            <th>Course</th>
                                            <th>Topic</th>
                                            <th>Offering</th>
                                            <th>Term</th>
                                            <th>Grade</th>
                                            <th>Credits</th>
                                            <th>In progress</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        @for (completion of completions(); track completion.completionId ?? $index) {
                                            <tr>
                                                <td>{{ completion.subject ?? "—" }}</td>
                                                <td>{{ completion.courseCode ?? "—" }}</td>
                                                <td>{{ completion.topicTitle ?? "—" }}</td>
                                                <td>{{ completion.offeringKey ?? "—" }}</td>
                                                <td>{{ completion.termCode ?? "—" }}</td>
                                                <td>{{ completion.grade ?? "—" }}</td>
                                                <td>{{ completion.credits ?? "—" }}</td>
                                                <td>{{ completion.isInProgress ? "Yes" : "No" }}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            }
                        </div>
                        <div>
                            <h3>Facts for this term</h3>
                            @if (!facts().length) {
                                <p class="c-text--secondary">No facts for this term.</p>
                            } @else {
                                <table class="c-table">
                                    <thead>
                                        <tr>
                                            <th>Fact</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        @for (fact of facts(); track fact.factId ?? $index) {
                                            <tr>
                                                <td>{{ fact.factCode }}</td>
                                                <td>{{ fact.factValue ?? "—" }}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            }
                        </div>
                    </div>
                }
            </section>
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .academic-home__page {
            padding: 1.5rem 1.75rem 2rem;
        }

        .academic-home__header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid var(--color--border);
        }

        .academic-home__header-copy {
            min-width: 0;
            flex: 1 1 16rem;
        }

        .academic-home__header-copy h1 {
            margin: 0 0 0.35rem;
            color: var(--color--text-strong);
        }

        .academic-home__header-copy p {
            margin: 0;
        }

        .academic-home__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }

        .academic-home__upload-input {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .academic-home__section {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 12px;
            padding: 1.25rem 1.5rem;
            margin-bottom: 1.25rem;
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 10%, transparent);
        }

        .academic-home__section:last-child {
            margin-bottom: 0;
        }

        .academic-home__section h2 {
            margin: 0 0 0.75rem;
            color: var(--color--text-strong);
        }

        .academic-home__section-heading {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }

        .academic-home__term-form {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            gap: 0.75rem;
        }

        .academic-home__term-form label,
        .academic-home__resolution {
            display: grid;
            gap: 0.25rem;
        }

        .academic-home__resolution {
            max-width: 24rem;
        }

        .academic-home__conflict-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
        }

        .academic-home__trees,
        .academic-home__student-cols {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.75rem;
        }

        .academic-home__trees > div,
        .academic-home__student-cols > div {
            flex: 1 1 20rem;
        }

        .academic-home__row--selected {
            background: var(--color--primary-flat-bg);
        }

        .c-table {
            width: 100%;
            border-collapse: collapse;
        }

        .c-table th,
        .c-table td {
            border: 1px solid var(--color--table-border);
            padding: 0.5rem 0.75rem;
            text-align: left;
        }

        .c-table th {
            background: var(--color--table-header);
        }

        .academic-home__student-picker {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            gap: 0.75rem;
        }

        .academic-home__student-label {
            display: grid;
            gap: 0.35rem;
            max-width: 24rem;
            flex: 1 1 16rem;
        }
    `,
})
export class AcademicHomeComponent {
    readonly labels = AcademicLabels;
    private readonly userStore = inject(UserStore);
    private readonly workbookUpload = viewChild<ElementRef<HTMLInputElement>>("workbookUpload");

    constructor() {
        TelemetryService.send("AcademicCurriculum", "Open");
        inject(DestroyRef).onDestroy(() => {
            if (this.studentRefreshHandle != null) clearTimeout(this.studentRefreshHandle);
        });
    }

    private readonly listGen = signal(0);
    private readonly resolutionGen = signal(0);
    private readonly conflictGen = signal(0);
    readonly importErrors = signal<string[]>([]);
    readonly importMessage = signal<string | null>(null);
    readonly newTermCode = signal("");
    readonly newTermDescription = signal("");
    readonly newTermStart = signal("");
    readonly newTermEnd = signal("");
    readonly selectedContact = signal<Nullable<DropDownItem>>(null);

    readonly termsResource = s25Resource({
        params: () => this.listGen(),
        loader: () => AcademicCurricApi.listTerms(),
    });

    readonly sortedTerms = computed((): AcademicTerm[] => {
        const terms = this.termsResource.value() ?? [];
        return [...terms].sort((termA, termB) => {
            const startCmp = (termA.startDt ?? "").localeCompare(termB.startDt ?? "");
            if (startCmp !== 0) return startCmp;
            const endCmp = (termA.endDt ?? "").localeCompare(termB.endDt ?? "");
            if (endCmp !== 0) return endCmp;
            return (termA.termCode ?? "").localeCompare(termB.termCode ?? "", undefined, { sensitivity: "base" });
        });
    });

    readonly selectedTermId = linkedSignal(() => this.sortedTerms()[0]?.termId ?? null);

    readonly cohortsResource = s25Resource({
        params: () => this.listGen(),
        loader: () => AcademicCurricApi.listCohorts(),
    });

    readonly cohorts = computed((): AcademicCohort[] => this.cohortsResource.value() ?? []);

    readonly selectedCohortId = linkedSignal(() => this.cohorts()[0]?.cohortId ?? null);

    readonly coursesResource = s25Resource({
        params: () => ({ gen: this.listGen(), cohortId: this.selectedCohortId() }),
        if: () => this.selectedCohortId() != null,
        loader: ({ params }) => AcademicCurricApi.listCourses(params.cohortId as number),
    });

    readonly programsResource = s25Resource({
        params: () => ({ gen: this.listGen(), cohortId: this.selectedCohortId() }),
        if: () => this.selectedCohortId() != null,
        loader: ({ params }) => AcademicCurricApi.listPrograms(params.cohortId as number),
    });

    readonly courses = computed((): AcademicCourse[] => this.coursesResource.value() ?? []);
    readonly programs = computed((): AcademicProgram[] => this.programsResource.value() ?? []);
    readonly selectedCourseId = linkedSignal(() => this.courses()[0]?.courseId ?? null);
    readonly selectedProgramId = linkedSignal(() => this.programs()[0]?.defnId ?? null);

    readonly selectedCourse = computed(() =>
        this.courses().find((course) => course.courseId === this.selectedCourseId()),
    );
    readonly selectedProgram = computed(() =>
        this.programs().find((program) => program.defnId === this.selectedProgramId()),
    );

    readonly courseTreeResource = s25Resource({
        params: () => this.selectedCourse()?.requisiteRootGroupId,
        if: () => this.selectedCourse()?.requisiteRootGroupId != null,
        loader: ({ params: rootGroupId }) => AcademicCurricApi.getRequirementRoot(rootGroupId as number),
    });

    readonly programTreeResource = s25Resource({
        params: () => this.selectedProgram()?.rootGroupId,
        if: () => this.selectedProgram()?.rootGroupId != null,
        loader: ({ params: rootGroupId }) => AcademicCurricApi.getRequirementRoot(rootGroupId as number),
    });

    readonly resolutionResource = s25Resource({
        params: () => this.resolutionGen(),
        loader: () => AcademicCurricApi.getTreeMergeResolution(),
    });

    readonly resolutionChoice = linkedSignal(
        (): TreeMergeResolutionPolicy => this.resolutionResource.value()?.resolution ?? "REVIEW",
    );

    readonly courseConflictsResource = s25Resource({
        params: () => ({
            gen: this.conflictGen(),
            rootGroupId: this.selectedCourse()?.requisiteRootGroupId,
        }),
        if: () => this.selectedCourse()?.requisiteRootGroupId != null,
        loader: ({ params }) => AcademicCurricApi.listConflicts(params.rootGroupId as number),
    });

    readonly programConflictsResource = s25Resource({
        params: () => ({
            gen: this.conflictGen(),
            rootGroupId: this.selectedProgram()?.rootGroupId,
        }),
        if: () => this.selectedProgram()?.rootGroupId != null,
        loader: ({ params }) => AcademicCurricApi.listConflicts(params.rootGroupId as number),
    });

    readonly courseConflicts = computed((): TreeMergeConflict[] => this.courseConflictsResource.value() ?? []);
    readonly programConflicts = computed((): TreeMergeConflict[] => this.programConflictsResource.value() ?? []);
    readonly savingResolution = signal(false);
    readonly resolvingConflictId = signal<number | null>(null);

    readonly selectedContId = signal<number | null>(null);
    private studentRefreshHandle: ReturnType<typeof setTimeout> | null = null;

    readonly completionsResource = s25Resource({
        params: () => this.selectedContId(),
        if: () => this.selectedContId() != null,
        loader: ({ params: contId }) => AcademicCurricApi.listCompletions(contId as number),
    });

    readonly factsResource = s25Resource({
        params: () => ({ contId: this.selectedContId(), termId: this.selectedTermId() }),
        if: () => this.selectedContId() != null && this.selectedTermId() != null,
        loader: ({ params }) => AcademicCurricApi.listFacts(params.contId as number, params.termId as number),
    });

    readonly courseEvalResource = s25Resource({
        params: () => ({
            courseId: this.selectedCourseId(),
            contId: this.selectedContId(),
            termId: this.selectedTermId(),
        }),
        if: () => this.selectedCourseId() != null && this.selectedContId() != null,
        loader: ({ params }) =>
            AcademicCurricApi.evaluateCourse(
                params.courseId as number,
                params.contId as number,
                params.termId ?? undefined,
            ),
    });

    readonly programEvalResource = s25Resource({
        params: () => ({
            defnId: this.selectedProgramId(),
            contId: this.selectedContId(),
            termId: this.selectedTermId(),
        }),
        if: () => this.selectedProgramId() != null && this.selectedContId() != null,
        loader: ({ params }) =>
            AcademicCurricApi.evaluateProgram(
                params.defnId as number,
                params.contId as number,
                params.termId ?? undefined,
            ),
    });

    readonly courseTree = computed(() => this.courseTreeResource.value()?.requirementTree);
    readonly programTree = computed(() => this.programTreeResource.value()?.requirementTree);
    readonly courseEditorTree = computed(() => {
        const tree = this.courseTree();
        return tree ? wireTreeToEditorNode(tree, undefined) : null;
    });
    readonly programEditorTree = computed(() => {
        const tree = this.programTree();
        return tree ? wireTreeToEditorNode(tree, undefined) : null;
    });
    readonly courseEvalTree = computed(() => this.courseEvalResource.value()?.curriculumTree ?? null);
    readonly programEvalTree = computed(() => this.programEvalResource.value()?.curriculumTree ?? null);
    readonly completions = computed(() => this.completionsResource.value() ?? []);
    readonly facts = computed(() => this.factsResource.value() ?? []);

    selectTerm(termId: number | undefined): void {
        if (termId != null) this.selectedTermId.set(termId);
    }

    selectCohort(cohortId: number): void {
        this.selectedCohortId.set(cohortId);
    }

    selectCourse(courseId: number): void {
        this.selectedCourseId.set(courseId);
    }

    selectProgram(defnId: number): void {
        this.selectedProgramId.set(defnId);
    }

    onStudentSelected(contact: Nullable<DropDownItem>): void {
        this.selectedContact.set(contact);
        this.scheduleStudentRefresh(contact);
    }

    clearStudent = (): void => {
        if (this.studentRefreshHandle != null) {
            clearTimeout(this.studentRefreshHandle);
            this.studentRefreshHandle = null;
        }
        this.selectedContact.set(null);
        this.selectedContId.set(null);
    };

    addTerm = async (): Promise<void> => {
        const termCode = this.newTermCode().trim();
        const startDt = this.newTermStart().trim();
        const endDt = this.newTermEnd().trim();
        if (!termCode || !startDt || !endDt) {
            this.importErrors.set(["Term code, start, and end are required."]);
            return;
        }
        if (startDt > endDt) {
            this.importErrors.set(["Start must be on or before end."]);
            return;
        }
        try {
            await AcademicCurricApi.saveTerm({
                termCode,
                description: this.newTermDescription().trim() || undefined,
                startDt: `${startDt}T00:00:00`,
                endDt: `${endDt}T00:00:00`,
            });
            this.newTermCode.set("");
            this.newTermDescription.set("");
            this.newTermStart.set("");
            this.newTermEnd.set("");
            this.importErrors.set([]);
            this.listGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    openWorkbookPicker = (): void => {
        this.workbookUpload()?.nativeElement.click();
    };

    downloadTemplate = async (): Promise<void> => {
        await AcademicWorkbookSpreadsheetUtil.downloadTemplate();
    };

    downloadWorkbook = async (): Promise<void> => {
        const workbook = await AcademicCurricApi.exportWorkbook();
        await AcademicWorkbookSpreadsheetUtil.download(workbook, "academic-curriculum.xlsx");
    };

    onWorkbookSelected = async (event: Event): Promise<void> => {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        try {
            const workbook = await AcademicWorkbookSpreadsheetUtil.readFile(file);
            const result = await AcademicCurricApi.importWorkbook(workbook);
            this.importErrors.set(result.errors ?? []);
            this.importMessage.set(result.errors?.length ? null : "Workbook imported.");
            TelemetryService.sendWithSub("AcademicCurriculum", "Workbook", "Complete");
            this.listGen.update((count) => count + 1);
            this.conflictGen.update((count) => count + 1);
        } catch (caughtError) {
            this.importMessage.set(null);
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            input.value = "";
        }
    };

    dateLabel(value: string | undefined): string {
        return formatUserprefDate(value, this.userStore.dateFormat());
    }

    fieldLabel(fieldName: string | undefined): string {
        return MERGE_FIELD_LABELS[fieldName ?? ""] ?? fieldName ?? "Field";
    }

    onResolutionChange = async (value: TreeMergeResolutionPolicy): Promise<void> => {
        const previous = this.resolutionResource.value()?.resolution ?? "REVIEW";
        this.resolutionChoice.set(value);
        if (this.savingResolution()) return;
        this.savingResolution.set(true);
        try {
            await AcademicCurricApi.saveTreeMergeResolution({ resolution: value });
            TelemetryService.sendWithSub("AcademicCurriculum", "TreeMerge", value);
            this.resolutionGen.update((count) => count + 1);
        } catch (caughtError) {
            this.resolutionChoice.set(previous);
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.savingResolution.set(false);
        }
    };

    keepOursFor(conflict: TreeMergeConflict): () => Promise<void> {
        return () => this.resolveConflict(conflict, false);
    }

    takeUploadFor(conflict: TreeMergeConflict): () => Promise<void> {
        return () => this.resolveConflict(conflict, true);
    }

    private async resolveConflict(conflict: TreeMergeConflict, takeIncoming: boolean): Promise<void> {
        if (conflict.conflictId == null || conflict.rootGroupId == null) return;
        if (this.resolvingConflictId() != null) return;
        this.resolvingConflictId.set(conflict.conflictId);
        try {
            await AcademicCurricApi.resolveConflict(conflict.rootGroupId, conflict.conflictId, takeIncoming);
            TelemetryService.sendWithSub(
                "AcademicCurriculum",
                "TreeMergeConflict",
                takeIncoming ? "TakeUpload" : "KeepOurs",
            );
            this.conflictGen.update((count) => count + 1);
            if (takeIncoming) this.listGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.resolvingConflictId.set(null);
        }
    }

    private scheduleStudentRefresh(contact: Nullable<DropDownItem>): void {
        if (this.studentRefreshHandle != null) {
            clearTimeout(this.studentRefreshHandle);
        }
        this.studentRefreshHandle = setTimeout(() => {
            this.studentRefreshHandle = null;
            const parsed = S25Util.parseInt(contact?.itemId);
            this.selectedContId.set(Number.isFinite(parsed) ? parsed : null);
        }, STUDENT_REFRESH_DELAY_MS);
    }
}
